import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLine, Overline, SectionHeading } from "../components/Reveal";
import { AbstractVisual, ParticleField } from "../components/AbstractVisual";

const WORKFLOW = ["Concept", "Research", "Storytelling", "Experience Design", "3D / Virtual Production", "XR Development", "Testing", "Deployment", "Distribution"];

const CAPABILITIES = [
  "VR Development", "AR Development", "Mixed Reality", "Free-Roaming XR", "Interactive Storytelling",
  "Virtual Production", "3D Environments", "Multiplayer Experiences", "Real-Time Engines",
  "Spatial Audio", "Motion Systems", "Educational XR", "Cultural XR", "Tourism Experiences", "Branded Immersive Experiences",
];

export default function Studio() {
  return (
    <main>
      <Seo title="XR Studio" path="/studio"
        description="XR production company India — from concept to immersive world. VR development, virtual production, free-roaming XR and interactive storytelling." />
      <section className="relative min-h-[80vh] flex items-end overflow-hidden">
        <AbstractVisual hue={195} seed={17} />
        <ParticleField hue={195} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030305] via-[#030305]/40 to-[#030305]/70" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 pb-24 w-full">
          <Overline className="mb-6">Vrova XR Studio</Overline>
          <h1 className="font-serif tracking-tight leading-[1.0] text-5xl md:text-7xl lg:text-8xl" data-testid="studio-headline">
            <MaskedLine delay={0.25}>From an Idea</MaskedLine>
            <MaskedLine delay={0.4}><span className="italic text-zinc-400">to an Immersive World.</span></MaskedLine>
          </h1>
          <p className="mt-8 max-w-xl text-zinc-400 font-light leading-relaxed">
            We collaborate with filmmakers, studios, museums, tourism boards, educational organizations and technology companies to develop historical, cultural, adventure, entertainment and educational immersive experiences.
          </p>
        </div>
      </section>

      <section className="py-28 md:py-40 border-t border-white/[0.06]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <SectionHeading overline="The Process" title={<>Nine Steps. <span className="italic text-zinc-400">One World.</span></>} />
          <div className="mt-20 space-y-0 border-t border-white/[0.07]" data-testid="studio-workflow">
            {WORKFLOW.map((step, i) => (
              <Reveal key={step} delay={i * 0.04}>
                <div className="group grid grid-cols-12 items-center gap-6 border-b border-white/[0.07] py-8 md:py-10 hover:bg-white/[0.02] transition-colors duration-500 px-2 md:px-6">
                  <p className="col-span-3 md:col-span-2 font-serif text-5xl md:text-7xl text-white/[0.07] group-hover:text-white/[0.16] transition-colors duration-500 select-none">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="col-span-9 md:col-span-6 font-serif text-2xl md:text-4xl tracking-tight group-hover:translate-x-2 transition-transform duration-500">{step}</h3>
                  <p className="hidden md:block col-span-4 text-sm text-zinc-500 font-light text-right">
                    {["The spark — theme, audience, ambition", "Deep dives into history, science, place", "Narrative architecture & emotional arcs", "Journey mapping, interaction, pacing", "Worlds built in engine, at true scale", "Code, tracking, multiplayer systems", "Relentless playtesting & refinement", "Venue installation & commissioning", "Vrova network takes it worldwide"][i]}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-28 md:py-36 border-t border-white/[0.06]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <SectionHeading overline="Capabilities" title={<>Everything an Immersive World <span className="italic text-zinc-400">Needs.</span></>} />
          <div className="flex flex-wrap gap-3 mt-14">
            {CAPABILITIES.map((c, i) => (
              <Reveal key={c} delay={i * 0.025}>
                <span data-testid={`capability-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="inline-block px-6 py-3.5 rounded-full bg-white/[0.03] border border-white/10 text-sm text-zinc-300 hover:border-white/30 transition-colors duration-300">
                  {c}
                </span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-24 text-center">
            <h3 className="font-serif text-3xl md:text-5xl tracking-tight">Have an idea? <span className="italic text-zinc-400">Let's build it together.</span></h3>
            <Link to="/contact?type=build" data-testid="start-project-cta"
              className="inline-flex items-center gap-2 mt-9 px-9 py-4 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors duration-300">
              Start a Project <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
